import { useQuery, useQueryClient } from '@tanstack/react-query'
import { baseURL, requestMiddleware, headers } from '@/v2/constants/requests'

export const useTimeOff = timeOffId => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['TimeOff', `${timeOffId}`],
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/time_offs/${timeOffId}`, {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        throw new Error("We couldn't fetch the time off")
      }
      const result = await res.json()
      return result
    },
    enabled: !!timeOffId,
  })

  return { timeOff: data, isLoading, isError }
}

export const useAddTimeOff = () => {
  const queryClient = useQueryClient()
  const { mutateAsync: addTimeOff, ...others } = requestMiddleware({
    key: ['AddTimeOff'],
    request: async timeOff =>
      await fetch(`${baseURL}/v2/rest/time_offs`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ time_off: timeOff }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TimeOff'] })
      queryClient.invalidateQueries({ queryKey: ['DailyCalendar'] })
      queryClient.invalidateQueries({ queryKey: ['WeeklyCalendar'] })
    },
  })

  return { addTimeOff, ...others }
}

export const useUpdateTimeOff = timeOffId => {
  const queryClient = useQueryClient()
  const { mutateAsync: updateTimeOff, ...others } = requestMiddleware({
    key: ['UpdateTimeOff', `${timeOffId}`],
    request: async payload =>
      await fetch(`${baseURL}/v2/rest/time_offs/${timeOffId}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ time_off: payload }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TimeOff', `${timeOffId}`] })
      queryClient.invalidateQueries({ queryKey: ['DailyCalendar'] })
      queryClient.invalidateQueries({ queryKey: ['WeeklyCalendar'] })
    },
  })

  return { updateTimeOff, ...others }
}

export const useRemoveTimeOff = timeOffId => {
  const queryClient = useQueryClient()
  const { mutateAsync: removeTimeOff, ...others } = requestMiddleware({
    key: ['RemoveTimeOff', `${timeOffId}`],
    request: async () =>
      await fetch(`${baseURL}/v2/rest/time_offs/${timeOffId}`, {
        method: 'DELETE',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: timeOffId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TimeOff'] })
      queryClient.invalidateQueries({ queryKey: ['DailyCalendar'] })
      queryClient.invalidateQueries({ queryKey: ['WeeklyCalendar'] })
    },
  })

  return { removeTimeOff, ...others }
}
